import { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [caps, setCaps] = useState([]);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [capsLoading, setCapsLoading] = useState(true);

  const loadCaps = async () => {
    setCapsLoading(true);
    try {
      const { data } = await api.get("/team/me/capabilities");
      setCaps(data?.capabilities || []);
      setRole(data?.role || null);
    } catch {
      setCaps([]);
      setRole(null);
    } finally {
      setCapsLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("realtify_token");
    if (!token) {
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then(async (r) => {
        setUser(r.data);
        await loadCaps();
      })
      .catch(() => {
        localStorage.removeItem("realtify_token");
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("realtify_token", data.access_token);
    setUser(data.user);
    await loadCaps();
    return data.user;
  };

  const signup = async (email, password, name) => {
    const { data } = await api.post("/auth/signup", { email, password, name });
    localStorage.setItem("realtify_token", data.access_token);
    setUser(data.user);
    await loadCaps();
    return data.user;
  };

  const setSession = async (data) => {
    // For invite acceptance and any other flow that returns a fresh {access_token, user} pair.
    if (data?.access_token) localStorage.setItem("realtify_token", data.access_token);
    if (data?.user) setUser(data.user);
    await loadCaps();
  };

  const logout = () => {
    localStorage.removeItem("realtify_token");
    setUser(null);
    setCaps([]);
    setRole(null);
    window.location.href = "/login";
  };

  const can = (cap) => caps.includes(cap);

  return (
    <AuthCtx.Provider value={{ user, loading, capsLoading, login, signup, logout, setSession, caps, role, can, reloadCaps: loadCaps }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
